import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import { ChevronRight, Flag, StickyNote, Target } from "lucide-react";
import {
  changeTone, formatCount, formatMetricShort, formatMetricValue, formatUsd, getOverview, getSeries, metricsUrls, previousRange, shortDate,
  type MetricCardData, type MetricUnit, type OverviewResponse, type ScopedParams, type SeriesResponse,
} from "@/lib/metrics";
import { CardsSkeleton, ChangeChip, EmptyState, ErrorState, Sparkline } from "./shared";

const HEADLINE = ["mrr", "customers", "net_revenue", "net_new_mrr"] as const;
const HERO_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "mrr", label: "MRR" },
  { id: "customers", label: "Customers" },
  { id: "net_revenue", label: "Net revenue" },
  { id: "new_customers", label: "New customers" },
];

/** A metric shown as one line in a list, not a card. */
function MetricRow({ card, onOpen, hint }: { card: MetricCardData | undefined; onOpen: (id: string) => void; hint?: string }) {
  if (!card) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(card.id)}
      className="group flex w-full items-center gap-3 py-2 text-left border-b last:border-0 hover:bg-muted/40 -mx-2 px-2 rounded-md transition-colors"
      data-testid={`row-metric-${card.id}`}
      title={card.description}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{card.label}</div>
        {hint ? <div className="text-[11px] text-muted-foreground truncate">{hint}</div> : null}
      </div>
      <div className="text-sm font-semibold tabular-nums">{formatMetricValue(card.value, card.unit)}</div>
      <div className="w-[9.5rem] shrink-0 text-right hidden sm:block">
        <ChangeChip metricId={card.id} change={card.change} changePct={card.changePct} unit={card.unit} />
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

function HeadlineTile({ card, onOpen }: { card: MetricCardData; onOpen: (id: string) => void }) {
  const tone = changeTone(card.id, card.change);
  return (
    <button
      type="button"
      onClick={() => onOpen(card.id)}
      className="text-left rounded-xl border bg-card p-4 hover:border-primary/60 hover:shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
      data-testid={`card-metric-${card.id}`}
      title={card.description}
    >
      <div className="text-xs font-medium text-muted-foreground">{card.label}</div>
      <div className="text-3xl font-bold mt-1 tabular-nums tracking-tight">{formatMetricValue(card.value, card.unit)}</div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <ChangeChip metricId={card.id} change={card.change} changePct={card.changePct} unit={card.unit} />
        <div className="w-24 shrink-0"><Sparkline points={card.spark} tone={tone} /></div>
      </div>
    </button>
  );
}

function HeroChart({ params, compare, cards }: { params: ScopedParams; compare: boolean; cards: Map<string, MetricCardData> }) {
  const [metric, setMetric] = useState<string>("mrr");
  const unit: MetricUnit = cards.get(metric)?.unit ?? "money";
  const prev = previousRange(params.from, params.to);
  const cur = useQuery<SeriesResponse>({ queryKey: [metricsUrls.series({ ...params, metric, granularity: "auto" })], queryFn: () => getSeries({ ...params, metric, granularity: "auto" }) });
  const before = useQuery<SeriesResponse>({
    queryKey: [metricsUrls.series({ ...params, ...prev, metric, granularity: cur.data?.granularity ?? "auto" })],
    queryFn: () => getSeries({ ...params, ...prev, metric, granularity: cur.data?.granularity ?? "auto" }),
    enabled: compare && !!cur.data,
  });
  const data = useMemo(() => {
    const points = cur.data?.points ?? [];
    const prevPoints = before.data?.points ?? [];
    return points.map((p, i) => ({ date: p.date, label: p.label.length === 10 ? p.label.slice(5) : p.label, value: p.value, previous: compare ? prevPoints[i]?.value ?? null : null }));
  }, [cur.data, before.data, compare]);
  const markers = useMemo(() => {
    const ann = cur.data?.annotations ?? [];
    return ann.map((a) => ({ id: a.id, title: a.title, label: data.find((d) => d.date >= a.date)?.label })).filter((m) => !!m.label);
  }, [cur.data, data]);
  const card = cards.get(metric);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{card?.label ?? "MRR"} over the period</CardTitle>
            <CardDescription>{compare ? "Solid is this period, dashed is the one before it." : "Turn on the comparison above to overlay the previous period."}</CardDescription>
          </div>
          <Tabs value={metric} onValueChange={setMetric}>
            <TabsList className="h-8">
              {HERO_OPTIONS.map((o) => <TabsTrigger key={o.id} value={o.id} className="text-xs h-6 px-2.5" data-testid={`hero-${o.id}`}>{o.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        {cur.isLoading ? (
          <div className="h-64 animate-pulse rounded-md bg-muted/40" />
        ) : cur.error ? (
          <ErrorState error={cur.error} />
        ) : data.length === 0 ? (
          <EmptyState title="Nothing to chart yet" hint="Widen the period or clear the segment." />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} width={64} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatMetricShort(v, unit)} />
                <ChartTooltip formatter={(v: any, n: any) => [v == null ? "–" : formatMetricValue(Number(v), unit), n === "previous" ? "Previous period" : card?.label ?? metric]} />
                <Area type="monotone" dataKey="value" name={card?.label ?? metric} stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#heroFill)" isAnimationActive={false} />
                {compare && <Line type="monotone" dataKey="previous" name="previous" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />}
                {markers.map((m) => (
                  <ReferenceLine key={m.id} x={m.label} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" label={{ value: m.title, fontSize: 10, position: "top", fill: "hsl(var(--muted-foreground))" }} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ListCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {description ? <CardDescription className="text-xs">{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="pt-1">{children}</CardContent>
    </Card>
  );
}

export default function ControlCenterTab({
  params, compare, onOpenMetric,
}: {
  params: ScopedParams;
  compare: boolean;
  onOpenMetric: (metricId: string) => void;
}) {
  const url = metricsUrls.overview({ ...params, compare });
  const { data, isLoading, error } = useQuery<OverviewResponse>({ queryKey: [url], queryFn: () => getOverview({ ...params, compare }) });

  if (isLoading) return <div className="space-y-4"><CardsSkeleton count={4} className="h-28" /><CardsSkeleton count={2} className="h-64" /></div>;
  if (error) return <Card><CardContent className="pt-6"><ErrorState error={error} hint="Check the sync status above, then try again." /></CardContent></Card>;
  if (!data) return null;
  if (data.cards.length === 0) {
    return <Card><CardContent className="pt-6"><EmptyState title="No metrics for this period yet" hint="Nothing has been mirrored from Stripe for this date range and segment. Widen the period, clear the segment, or run a sync." /></CardContent></Card>;
  }

  const cards = new Map(data.cards.map((c) => [c.id, c]));
  const get = (id: string) => cards.get(id);
  const mrrHint = (id: string) => { const c = get(id); return c ? formatMetricValue(c.value, c.unit) : undefined; };
  const goals = data.goals.filter((g) => !g.achieved || (g.achievedAt && Date.now() - new Date(g.achievedAt).getTime() < 14 * 86400000));
  const annotations = [...data.annotations].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-4" data-testid="tab-control-center">
      {/* 1. The four numbers that matter */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {HEADLINE.map((id) => { const c = get(id); return c ? <HeadlineTile key={id} card={c} onOpen={onOpenMetric} /> : null; })}
      </div>

      {/* 2. One chart */}
      <HeroChart params={params} compare={compare} cards={cards} />

      {/* 3. Where MRR moved, and whether the business is healthy */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ListCard title="How MRR moved" description="Customers gained and lost in the period, with the MRR each movement carried. Click a line for the people.">
          <MetricRow card={get("new_customers")} onOpen={onOpenMetric} hint={`New MRR ${mrrHint("mrr_new") ?? ""}`} />
          <MetricRow card={get("reactivations")} onOpen={onOpenMetric} hint={`Reactivation MRR ${mrrHint("mrr_reactivation") ?? ""}`} />
          <MetricRow card={get("upgrades")} onOpen={onOpenMetric} hint={`Expansion MRR ${mrrHint("mrr_expansion") ?? ""}`} />
          <MetricRow card={get("downgrades")} onOpen={onOpenMetric} hint={`Contraction MRR ${mrrHint("mrr_contraction") ?? ""}`} />
          <MetricRow card={get("cancellations")} onOpen={onOpenMetric} hint={`Churned MRR ${mrrHint("mrr_churn") ?? ""}`} />
        </ListCard>
        <ListCard title="Health" description="The ratios Baremetrics watches. Down is good for churn; a quick ratio above 4 means growth is comfortably outrunning losses.">
          <MetricRow card={get("customer_churn_rate")} onOpen={onOpenMetric} />
          <MetricRow card={get("revenue_churn_rate")} onOpen={onOpenMetric} />
          <MetricRow card={get("quick_ratio")} onOpen={onOpenMetric} />
          <MetricRow card={get("arpu")} onOpen={onOpenMetric} />
          <MetricRow card={get("ltv")} onOpen={onOpenMetric} />
          <MetricRow card={get("arr")} onOpen={onOpenMetric} />
        </ListCard>
      </div>

      {/* 4. The rest, in three quiet lists */}
      <div className="grid gap-4 md:grid-cols-3">
        <ListCard title="Revenue detail">
          <MetricRow card={get("gross_revenue")} onOpen={onOpenMetric} />
          <MetricRow card={get("fees")} onOpen={onOpenMetric} />
          <MetricRow card={get("refunds")} onOpen={onOpenMetric} />
          <div className="flex items-center justify-between py-2 text-sm border-t mt-1">
            <span className="text-muted-foreground">Settled in USD</span>
            <span className="font-semibold tabular-nums">{formatUsd(data.extras.netRevenueUsd)}</span>
          </div>
        </ListCard>
        <ListCard title="Collections">
          <MetricRow card={get("payment_success_rate")} onOpen={onOpenMetric} />
          <MetricRow card={get("failed_charges")} onOpen={onOpenMetric} />
          <MetricRow card={get("failed_amount")} onOpen={onOpenMetric} />
          <div className="flex items-center justify-between py-2 text-sm border-t mt-1">
            <span className="text-muted-foreground">Paused · Disputes</span>
            <span className={`font-semibold tabular-nums ${data.extras.disputes > 0 ? "text-destructive" : ""}`}>{formatCount(data.extras.paused)} · {formatCount(data.extras.disputes)}</span>
          </div>
        </ListCard>
        <ListCard title="Trials">
          <MetricRow card={get("trial_starts")} onOpen={onOpenMetric} />
          <MetricRow card={get("trial_conversions")} onOpen={onOpenMetric} />
          <MetricRow card={get("trial_conversion_rate")} onOpen={onOpenMetric} />
          <MetricRow card={get("active_trials")} onOpen={onOpenMetric} />
        </ListCard>
      </div>

      {/* 5. Goals and notes only when there are any */}
      {(goals.length > 0 || annotations.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {goals.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4" />Goals</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {goals.map((goal) => (
                  <div key={goal.id} className="space-y-1.5" data-testid={`goal-strip-${goal.id}`}>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="font-medium truncate">{goal.label || goal.metric}</span>
                      <span className="text-xs text-muted-foreground tabular-nums shrink-0">{formatMetricValue(goal.value, goal.unit)} of {formatMetricValue(goal.target, goal.unit)}</span>
                    </div>
                    <Progress value={Math.max(0, Math.min(100, goal.progress))} className={goal.achieved || goal.onTrack ? "" : "[&>div]:bg-destructive"} />
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span>{goal.achieved ? `Hit ${shortDate(goal.achievedAt)}` : `${goal.daysLeft} day${goal.daysLeft === 1 ? "" : "s"} left`}</span>
                      <Badge variant="outline" className={`text-[10px] py-0 ${goal.achieved || goal.onTrack ? "text-emerald-700 dark:text-emerald-400" : "text-destructive"}`}>{goal.achieved ? "Achieved" : goal.onTrack ? "On track" : "Behind"}</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          {annotations.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><StickyNote className="w-4 h-4" />What happened in this period</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {annotations.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 text-sm" data-testid={`annotation-${a.id}`}>
                      <span className="text-xs text-muted-foreground w-14 shrink-0 tabular-nums">{shortDate(a.date)}</span>
                      <span className="truncate">{a.title}</span>
                      {a.auto && <Flag className="w-3 h-3 text-muted-foreground shrink-0" aria-label="Added automatically" />}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
